import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, RefreshCw, Calendar, Clock, Target, ExternalLink, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useTriggerLogs } from '@/hooks/queries/useTriggerLogs';
import { TriggerLogsSummary } from './TriggerLogsSummary';
import { WebhookLogEntry } from '@/utils/webhookLogger';
import { formatDistanceToNow } from 'date-fns';

interface TriggerLogsTabProps {
  chatbotId?: string;
}

export const TriggerLogsTab: React.FC<TriggerLogsTabProps> = ({
  chatbotId
}) => {
  const { logs, summary, loading, error, refreshLogs, clearLogs } = useTriggerLogs(chatbotId);
  const [selectedTrigger, setSelectedTrigger] = useState<string>('all');

  if (!chatbotId) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <p className="text-sm">No chatbot selected</p>
          <p className="text-xs mt-1">Select a chatbot to view trigger execution logs</p>
        </div>
      </div>
    );
  }

  const handleClearLogs = async () => {
    if (window.confirm('Are you sure you want to clear all trigger logs for this chatbot? This action cannot be undone.')) {
      await clearLogs();
    }
  };

  const filteredLogs = selectedTrigger === 'all'
    ? logs
    : logs.filter(log => log.trigger_name === selectedTrigger);

  const getStatusIcon = (log: WebhookLogEntry) => {
    if (log.response_status === undefined) {
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
    if (log.response_status >= 200 && log.response_status < 300) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  const getStatusBadge = (log: WebhookLogEntry) => {
    if (log.response_status === undefined) {
      return <Badge variant="secondary">Pending</Badge>;
    }
    if (log.response_status >= 200 && log.response_status < 300) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Success</Badge>;
    }
    return <Badge variant="destructive">Failed</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary.totalExecutions > 0 && (
        <TriggerLogsSummary summary={summary} />
      )}

      {/* Logs Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Trigger Execution Logs</CardTitle>
              <Badge variant="outline">{filteredLogs.length} events</Badge>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedTrigger}
                onChange={(e) => setSelectedTrigger(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Triggers</option>
                {logs.map(log => log.trigger_name).filter((value, index, self) => self.indexOf(value) === index).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshLogs}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {logs.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearLogs}
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading logs</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {loading && logs.length === 0 && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm">Loading trigger logs...</p>
              </div>
            </div>
          )}
          {filteredLogs.length === 0 && !loading && !error && (
            <div className="text-center py-8 text-gray-500">
              <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium mb-1">No trigger events yet</p>
              <p className="text-sm">
                {logs.length === 0
                  ? "Trigger executions will appear here when conditions are met during conversations."
                  : "No events found for the selected trigger filter."
                }
              </p>
            </div>
          )}
          {filteredLogs.length > 0 && (
            <div className="space-y-3">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="mt-1">
                    {getStatusIcon(log)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">
                        {log.trigger_name || 'Unknown Trigger'}
                      </span>
                      {getStatusBadge(log)}
                      {log.execution_time_ms !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {log.execution_time_ms}ms
                        </Badge>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                        </span>
                        <span className="text-xs text-gray-500">
                          Session: {log.session_id?.slice(-8) || 'Unknown'}
                        </span>
                        {log.response_status && (
                          <span className="text-xs text-gray-500">
                            HTTP {log.response_status}
                          </span>
                        )}
                      </div>
                    </div>

                    {log.webhook_url && (
                      <div className="text-xs text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          {log.webhook_url.length > 50
                            ? `${log.webhook_url.slice(0, 50)}...`
                            : log.webhook_url
                          }
                        </span>
                      </div>
                    )}

                    {log.error_message && (
                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded border">
                        <strong>Error:</strong> {log.error_message}
                      </div>
                    )}

                    {/* Payload and Response Data Section */}
                    <div className="mt-3 space-y-2">
                      {/* Payload Sent */}
                      {log.payload && typeof log.payload === 'object' && (
                        <details className="text-xs">
                          <summary className="cursor-pointer hover:text-gray-800 flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <span className="font-medium text-blue-700">📤 Payload Sent</span>
                            <span className="text-blue-600">
                              ({Object.keys(log.payload).length} fields)
                            </span>
                          </summary>
                          <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="mb-2">
                              <span className="font-medium text-blue-700">Request Details:</span>
                              <div className="text-blue-600 text-xs mt-1">
                                <div>Method: POST</div>
                                <div>Content-Type: application/json</div>
                                <div>URL: {log.webhook_url}</div>
                              </div>
                            </div>
                            <div className="mb-2">
                              <span className="font-medium text-blue-700">Payload Structure:</span>
                              <div className="text-blue-600 text-xs mt-1">
                                {Object.keys(log.payload).map((key, index) => (
                                  <div key={index} className="flex justify-between">
                                    <span>{key}:</span>
                                    <span className="text-gray-500">
                                      {typeof log.payload[key]}
                                      {Array.isArray(log.payload[key]) && ` (${log.payload[key].length} items)`}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <pre className="p-2 bg-white rounded text-xs overflow-x-auto border border-blue-300 text-gray-800">
                              {JSON.stringify(log.payload, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}

                      {/* Response Received */}
                      {(log.response_data || log.response_status) && (
                        <details className="text-xs">
                          <summary className="cursor-pointer hover:text-gray-800 flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                            <span className="font-medium text-green-700">📥 Response Received</span>
                            {log.response_status && (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                log.response_status >= 200 && log.response_status < 300
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                HTTP {log.response_status}
                              </span>
                            )}
                            {log.execution_time_ms && (
                              <span className="text-green-600">
                                ({log.execution_time_ms}ms)
                              </span>
                            )}
                          </summary>
                          <div className="mt-2 p-3 bg-green-50 rounded border border-green-200">
                            <div className="mb-2">
                              <span className="font-medium text-green-700">Response Details:</span>
                              <div className="text-green-600 text-xs mt-1">
                                <div>Status: {log.response_status || 'Unknown'}</div>
                                <div>Execution Time: {log.execution_time_ms ? `${log.execution_time_ms}ms` : 'Unknown'}</div>
                                <div>Timestamp: {new Date(log.timestamp).toLocaleString()}</div>
                              </div>
                            </div>
                            {log.response_data && typeof log.response_data === 'object' && (
                              <>
                                <div className="mb-2">
                                  <span className="font-medium text-green-700">Response Data:</span>
                                </div>
                                <pre className="p-2 bg-white rounded text-xs overflow-x-auto border border-green-300 text-gray-800">
                                  {JSON.stringify(log.response_data, null, 2)}
                                </pre>
                              </>
                            )}
                            {!log.response_data && (
                              <div className="text-green-600 text-xs italic">
                                No response data received
                              </div>
                            )}
                          </div>
                        </details>
                      )}

                      {/* PII Data (if present) */}
                      {log.pii_data && typeof log.pii_data === 'object' && Object.keys(log.pii_data).length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer hover:text-gray-800 flex items-center gap-2 p-2 bg-purple-50 rounded border border-purple-200">
                            <span className="font-medium text-purple-700">🔒 PII Data</span>
                            <span className="text-purple-600">
                              ({Object.keys(log.pii_data).length} fields)
                            </span>
                          </summary>
                          <div className="mt-2 p-3 bg-purple-50 rounded border border-purple-200">
                            <div className="mb-2 text-purple-700 text-xs">
                              <strong>⚠️ Sensitive Data:</strong> Personal information included in webhook
                            </div>
                            <pre className="p-2 bg-white rounded text-xs overflow-x-auto border border-purple-300 text-gray-800">
                              {JSON.stringify(log.pii_data, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
